import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ItemDescriptionProviderService } from 'src/app/providers/item-description/item-description.provider.service';

/**
 * Sent instead of a real description when the lookup cannot be completed.
 *
 * The description is load bearing — B2B rejects an add-to-cart with an empty one — and B2B has no
 * way to validate the content, since the whole reason it is sent is that B2B does not know it. So
 * the failure mode is deliberately a visible sentinel rather than a plausible-looking guess: seeing
 * this on a B2B order line means the lookup failed for that item and wants investigating.
 */
export const FALLBACK_ITEM_DESCRIPTION = 'Custom Ring';

const RETRY_DELAYS_MS = [300, 900];

/**
 * Fetches and caches the B2B add-to-cart description.
 *
 * Three things keep a network call off the critical path of checkout:
 *  - `prefetch` is called when the review step opens, so the value is normally already in hand by
 *    the time Add to Cart is clicked;
 *  - results are cached per item, so the click awaits an already-settled promise;
 *  - failures fall back to FALLBACK_ITEM_DESCRIPTION rather than throwing, because blocking
 *    checkout is worse than an order line that needs a look.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemDescriptionService {
  private provider = inject(ItemDescriptionProviderService);
  private cache = new Map<string, Promise<string>>();

  /** Warm the cache without awaiting. Safe to call repeatedly. */
  prefetch(itemNumber: string, retailerIframeID: string | null) {
    void this.get(itemNumber, retailerIframeID);
  }

  get(itemNumber: string, retailerIframeID: string | null): Promise<string> {
    if (!retailerIframeID) {
      // Without a key the request cannot be built at all. Degrade rather than block checkout.
      console.error('No retailer iframe ID; sending the fallback item description');
      return Promise.resolve(FALLBACK_ITEM_DESCRIPTION);
    }

    const key = `${retailerIframeID}|${itemNumber}`;
    let pending = this.cache.get(key);
    if (!pending) {
      pending = this.fetchWithRetry(itemNumber, retailerIframeID);
      this.cache.set(key, pending);
    }
    return pending;
  }

  private async fetchWithRetry(itemNumber: string, retailerIframeID: string): Promise<string> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.provider.get(itemNumber, retailerIframeID);
      } catch (error) {
        const isLastAttempt = attempt >= RETRY_DELAYS_MS.length;
        if (!this.isTransient(error) || isLastAttempt) {
          console.error(
            `Item description lookup failed for ${itemNumber} after ${attempt + 1} attempt(s); ` +
            `sending "${FALLBACK_ITEM_DESCRIPTION}"`, error);
          return FALLBACK_ITEM_DESCRIPTION;
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
      }
    }
  }

  /**
   * Only retry what a retry can fix. A 404 means this item does not resolve on that endpoint and
   * will not resolve on the next attempt either — retrying it would just delay a certain failure,
   * and this sits on the money path where delay is itself harm.
   */
  private isTransient(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || error.status >= 500;
    }
    return false;
  }
}
